import { i18nNsFactory } from '@atlas-frontend/i18n';
import {
  AccessGroupListDto,
  cleanArray,
  DirectoryTableProps,
  Table,
  TableOperationButton,
  TextEllipsis,
  useIsAdmin,
} from '@atlas-frontend/sdk';
import { useMemo } from 'react';
import translations from './AccessGroupDirectoryTable.translations.json';
import { createColumnHelper } from '@tanstack/react-table';
import { UiLink } from '../../paths/UiLink';
import { path } from '../../paths';
import { Flex } from 'antd';
import { colorGray } from '@atlas-frontend/design-tokens';
import * as Icon from 'react-feather';
import { useNavigateTo } from '../../paths/useNavigateTo';
import './RoleInfiniteFilterInput';

const { addTranslations, useTranslation } = i18nNsFactory(
  'access-group-directory-table',
);
addTranslations(translations);

const columnHelper = createColumnHelper<AccessGroupListDto>();

export type AccessGroupDirectoryTableProps = DirectoryTableProps<any>;

export function AccessGroupDirectoryTable(
  props: AccessGroupDirectoryTableProps,
) {
  const { t } = useTranslation();
  const navigateToAccessGroupCreate = useNavigateTo(path.accessGroupCreate);
  const hasPermissionToManage = useIsAdmin();

  const columns = useMemo(
    () =>
      cleanArray([
        columnHelper.accessor('name', {
          header: t('column.name'),
          cell: (props) => (
            <TextEllipsis>
              <UiLink
                id={`access-group-${props.row.index}`}
                path={path.accessGroupDetails}
                params={{ id: props.row.original.id }}
              >
                {props.renderValue()}
              </UiLink>
            </TextEllipsis>
          ),
          meta: {
            width: '40ch',
          },
        }),
        columnHelper.accessor('roles', {
          id: 'roleId',
          header: t('column.roles'),
          enableSorting: false,
          filterFn: 'roleIds',
        }),
        columnHelper.accessor('description', {
          header: t('column.description'),
          meta: {
            minWidth: '30ch',
          },
        }),
        hasPermissionToManage &&
          columnHelper.display({
            id: 'operations',
            header: () => (
              <Flex justify="center" gap="small">
                <TableOperationButton
                  id="create-access-group"
                  icon={<Icon.Plus size={22} color={colorGray[7]} />}
                  onClick={() => navigateToAccessGroupCreate()}
                  title={t('button.add-access-group')}
                />
              </Flex>
            ),
            enableSorting: false,
            enableColumnFilter: false,
            meta: {
              width: 50,
              className: 'sticky-right',
            },
          }),
      ]),
    [navigateToAccessGroupCreate, t, hasPermissionToManage],
  );

  return (
    <Table
      name="access-group-directory-table"
      columns={columns}
      hideClearFilters
      manualFiltering
      manualSorting
      scrollContainerSelector="#access-group-content"
      sticky
      virtual
      {...props}
      isLoading={props.isLoading}
    />
  );
}
